// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon, { ICONS } from '../../icons';
import MarkButtons, { type AssetMark } from '../../displays/MarkButtons';
import AudioWaveformPlaceholder from '../../AudioWaveformPlaceholder';
import Model3DPlaceholder from '../../displays/Model3DPlaceholder';
import LazyModel3DViewer from '../../displays/LazyModel3DViewer';
import { useLocalization, type Language } from '../../../../config/localization';
import type { GenerationSlot } from '../../../../contexts/WidgetContext';
import CollectionSelectModal from './CollectionSelectModal';
import { buildDownloadFilename } from '../../../../utils/downloadNaming';
import { resolveIs3D } from '../../../../utils/mediaTypeDetection';
import { convertBlobToWebP } from '../../../../utils/imageWebp';
import { extractGenerationParams } from '../../../pages/gallery/galleryUtils';
import { resolveSlotGenerationId } from '../../../../utils/markGeneration';
import { isDraftFinalCandidate } from '../../../../utils/draftFinal';
import { fetchModelStats, type ModelStats } from '../../../../utils/glbStats';

interface ImageCollection {
  id: number;
  name: string;
  is_default: boolean;
}

interface Collection {
  id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  is_private: boolean;
  can_edit: boolean;
  total_images?: number;
}

interface MediaPreviewModalProps {
  isOpen: boolean;
  slot: GenerationSlot | null;
  onClose: () => void;
  onToggleFavorite: (id: string) => void;
  onMark?: (id: string, mark: AssetMark | null) => void;
  /** Turn a finished draft into a 1080p final; only the workbench passes it. */
  onDraftFinal?: (slot: GenerationSlot) => void;
  onMoveToTrash?: (id: number) => void;
  onRecreate?: () => void;
  onRemoveFromCollection?: (imageId: number, collectionId: number) => Promise<void>;
  collections?: Collection[];
  onAddToCollections?: (imageId: number, collectionIds: number[]) => Promise<void>;
  language: Language;
  // Slot navigation (optional - for session gallery)
  allSlots?: GenerationSlot[];
  currentSlotIndex?: number;
  onSlotNavigate?: (index: number) => void;
  // Loading indicator for auto-loading more images
  isLoadingMore?: boolean;
}

export default function MediaPreviewModal({
  isOpen,
  slot,
  onClose,
  onToggleFavorite,
  onMark,
  onDraftFinal,
  onMoveToTrash,
  onRecreate,
  onRemoveFromCollection,
  collections = [],
  onAddToCollections,
  language,
  allSlots,
  currentSlotIndex,
  onSlotNavigate,
  isLoadingMore
}: MediaPreviewModalProps) {
  const t = useLocalization(language);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [removingCollectionId, setRemovingCollectionId] = useState<number | null>(null);
  const [showAddCollectionModal, setShowAddCollectionModal] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [downloadWebPSuccess, setDownloadWebPSuccess] = useState(false);
  const [textCopied, setTextCopied] = useState(false);
  const [modelStats, setModelStats] = useState<ModelStats | null>(null);
  const [modelDimensions, setModelDimensions] = useState<{
    modelUrl: string | undefined;
    value: { x: number; y: number; z: number };
  } | null>(null);

  // Reference image zoom preview state (long press / hold to zoom)
  const [zoomPreviewUrl, setZoomPreviewUrl] = useState<string | null>(null);
  const longPressTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const LONG_PRESS_DELAY = 400; // ms

  // Long press handlers for reference image preview
  const handleRefImagePressStart = (e: React.MouseEvent | React.TouchEvent, imageUrl: string) => {
    e.stopPropagation();
    e.preventDefault();
    longPressTimerRef.current = setTimeout(() => {
      setZoomPreviewUrl(imageUrl);
    }, LONG_PRESS_DELAY);
  };

  const handleRefImagePressEnd = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Only clear the timer - don't close preview here
    // Preview closes via the overlay's onMouseUp/onClick
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleCloseZoomPreview = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setZoomPreviewUrl(null);
  };

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // Slot navigation computed values
  const hasSlotNavigation = allSlots && allSlots.length > 1 && onSlotNavigate;
  const slotCount = allSlots?.length || 0;

  // Slot navigation functions
  const goToPreviousSlot = () => {
    if (hasSlotNavigation && currentSlotIndex !== undefined) {
      const newIndex = currentSlotIndex > 0 ? currentSlotIndex - 1 : slotCount - 1;
      onSlotNavigate(newIndex);
    }
  };

  const goToNextSlot = () => {
    if (hasSlotNavigation && currentSlotIndex !== undefined) {
      const newIndex = currentSlotIndex < slotCount - 1 ? currentSlotIndex + 1 : 0;
      onSlotNavigate(newIndex);
    }
  };

  // Parse collections from JSON string
  const parsedCollections: ImageCollection[] = React.useMemo(() => {
    if (!slot?.metadata?.collections) return [];

    try {
      const parsed = JSON.parse(slot.metadata.collections);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // Parsing failed - will use fallback
    }
    return [];
  }, [slot?.metadata?.collections]);

  // Fallback to raw string if parsing failed (for backwards compatibility)
  const collectionsRawString = !parsedCollections.length ? slot?.metadata?.collections : null;

  // Parse prompt_v2 data from rawInput for displaying references
  // Returns: summary chips (grouped by category) and all reference images
  interface PromptV2Summary {
    chips: { label: string; values: string[] }[];  // e.g., { label: "Objects", values: ["Tree"] }
    allImages: string[];  // All reference images collected
  }

  const promptV2Summary = React.useMemo((): PromptV2Summary => {
    if (!slot?.rawInput) return { chips: [], allImages: [] };

    try {
      const rawInput = JSON.parse(slot.rawInput);
      // Check both direct path and nested path (inside inputs object)
      const promptV2 = rawInput.prompt_v2 || rawInput.inputs?.prompt_v2;
      if (!promptV2 || typeof promptV2 !== 'object') return { chips: [], allImages: [] };

      const chips: { label: string; values: string[] }[] = [];
      const allImages: string[] = [];

      // Track categories for default mode
      const categoryValues: Record<string, string[]> = {};
      // Track characters for character mode
      const characterValues: Record<string, string[]> = {};

      for (const [key, value] of Object.entries(promptV2)) {
        // Skip the main prompt field
        if (key === 'prompt') continue;

        // Handle character mode data
        if (key === 'prompt_characters' && typeof value === 'object' && value !== null) {
          const charactersData = value as Record<string, Record<string, { prompt_required?: string; prompt_optional?: string; image_urls?: string[]; subcategory_name?: string }>>;
          for (const [charName, charSelections] of Object.entries(charactersData)) {
            // Format character name: "Character#1" -> "Character #1"
            const formattedCharName = charName.replace('#', ' #');

            for (const [subcatKey, subcatData] of Object.entries(charSelections)) {
              if (subcatData && typeof subcatData === 'object') {
                // Collect images
                if (subcatData.image_urls && subcatData.image_urls.length > 0) {
                  allImages.push(...subcatData.image_urls);
                }
                // Use subcategory_name if available, otherwise format the key
                const subcatName = subcatData.subcategory_name || subcatKey.replace(/_/g, ' ');
                // Capitalize first letter
                const displayName = subcatName.charAt(0).toUpperCase() + subcatName.slice(1);

                if (!characterValues[formattedCharName]) {
                  characterValues[formattedCharName] = [];
                }
                characterValues[formattedCharName].push(displayName);
              }
            }
          }
          continue;
        }

        // Handle default mode categories
        if (typeof value === 'object' && value !== null) {
          const catData = value as { prompt_required?: string; prompt_optional?: string; image_urls?: string[]; category_name?: string; subcategory_name?: string };

          // Collect images
          if (catData.image_urls && catData.image_urls.length > 0) {
            allImages.push(...catData.image_urls);
          }

          // Get category name from data or format the key
          const categoryName = catData.category_name || key.replace(/_/g, ' ');
          // Capitalize first letter
          const displayCatName = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);

          // Only add subcategory value if it exists and is different from category
          if (catData.subcategory_name) {
            const displaySubcatName = catData.subcategory_name.charAt(0).toUpperCase() + catData.subcategory_name.slice(1);
            if (!categoryValues[displayCatName]) {
              categoryValues[displayCatName] = [];
            }
            categoryValues[displayCatName].push(displaySubcatName);
          } else {
            // No subcategory name - just mark the category as selected (empty array will show just the category)
            if (!categoryValues[displayCatName]) {
              categoryValues[displayCatName] = [];
            }
          }
        }
      }

      // Convert category values to chips (for default mode)
      for (const [category, values] of Object.entries(categoryValues)) {
        chips.push({ label: category, values });
      }

      // Convert character values to chips (for character mode)
      for (const [character, values] of Object.entries(characterValues)) {
        chips.push({ label: character, values });
      }

      return { chips, allImages };
    } catch (e) {
      console.error('Failed to parse prompt_v2 data:', e);
      return { chips: [], allImages: [] };
    }
  }, [slot?.rawInput]);

  const generationParams = React.useMemo(
    () => extractGenerationParams(slot?.rawInput, {
      booleanTrue: t.galleryPage?.booleanYes || 'Yes',
      booleanFalse: t.galleryPage?.booleanNo || 'No',
    }),
    [slot?.rawInput, t.galleryPage?.booleanYes, t.galleryPage?.booleanNo]
  );

  // Get all URLs for gallery
  const allPreviewUrls = slot?.previewUrls || (slot?.previewUrl ? [slot.previewUrl] : []);
  const allOriginalUrls = slot?.originalUrls || (slot?.originalUrl ? [slot.originalUrl] : []);
  const allOriginalUrlsUnproxied = slot?.metadata?.allOriginalUrlsUnproxied ||
    (slot?.metadata?.originalUrlUnproxied ? [slot.metadata.originalUrlUnproxied] : []);

  const hasMultipleImages = allPreviewUrls.length > 1;
  const imageCount = allPreviewUrls.length;

  const resolveAssetUrls = (index: number) => {
    // The singular URL fields represent the first asset. Reusing them for a
    // later selectable asset when the URL arrays have different lengths would
    // select the first asset again instead of that asset's own preview URL.
    // A single-asset slot can briefly retain an old index while slots change,
    // so its singular fallback remains safe at any index.
    const useSingularFallback = index === 0 || !hasMultipleImages;
    const previewUrl = allPreviewUrls[index]
      || (useSingularFallback ? slot?.previewUrl : undefined);
    const originalUrl = allOriginalUrls[index]
      || (useSingularFallback ? slot?.originalUrl : undefined);
    const originalUnproxied = allOriginalUrlsUnproxied[index]
      || (useSingularFallback ? slot?.metadata?.originalUrlUnproxied : undefined);

    return {
      previewUrl,
      originalUrl,
      originalUnproxied,
      assetUrl: originalUnproxied || originalUrl || previewUrl,
    };
  };

  // Get current asset URLs based on the selected index. These values stay
  // hook-safe even while the modal is closed or has no slot.
  const {
    previewUrl: currentPreviewUrl,
    originalUrl: currentOriginalUrl,
    originalUnproxied: currentOriginalUnproxied,
    assetUrl: currentAssetUrl,
  } = resolveAssetUrls(selectedIndex);

  // 3D is decided per asset because a 3D generation can include companion
  // PNGs that must continue to render as images.
  const is3D = resolveIs3D(slot?.contentType, currentAssetUrl);
  const model3dUrl = currentAssetUrl;
  // Explicit null check, not `modelDimensions?.modelUrl === model3dUrl`: for a
  // slot with no asset URLs `model3dUrl` is undefined, so the optional chain
  // would make the comparison `undefined === undefined` and read `.value` off
  // the null state — crashing the modal for text/empty slots.
  const currentModelDimensions = modelDimensions && modelDimensions.modelUrl === model3dUrl
    ? modelDimensions.value
    : null;

  // Reset selected index and copy state when modal opens with new slot
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
      setPromptCopied(false);
    }
  }, [isOpen, slot?.id]);

  useEffect(() => {
    if (!isOpen) setModelDimensions(null);
  }, [isOpen]);

  useEffect(() => {
    setModelStats(null);
    if (!isOpen || !is3D || !model3dUrl) return;

    const controller = new AbortController();
    void fetchModelStats(model3dUrl, controller.signal).then((stats) => {
      if (!controller.signal.aborted) setModelStats(stats);
    });

    return () => {
      controller.abort();
    };
  }, [isOpen, is3D, model3dUrl]);

  // Handle ESC key to close modal, arrow keys for navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        // ArrowLeft: navigate within slot if multiple images, otherwise between slots
        if (hasMultipleImages) {
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : imageCount - 1));
        } else if (hasSlotNavigation) {
          goToPreviousSlot();
        }
      } else if (event.key === 'ArrowRight') {
        // ArrowRight: navigate within slot if multiple images, otherwise between slots
        if (hasMultipleImages) {
          setSelectedIndex(prev => (prev < imageCount - 1 ? prev + 1 : 0));
        } else if (hasSlotNavigation) {
          goToNextSlot();
        }
      } else if (event.key === 'ArrowUp' && hasSlotNavigation) {
        // ArrowUp: always navigate to previous slot
        event.preventDefault();
        goToPreviousSlot();
      } else if (event.key === 'ArrowDown' && hasSlotNavigation) {
        // ArrowDown: always navigate to next slot
        event.preventDefault();
        goToNextSlot();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, hasMultipleImages, imageCount, hasSlotNavigation, goToPreviousSlot, goToNextSlot]);

  if (!isOpen || !slot || slot.status !== 'completed') {
    return null;
  }

  // Markable only when the slot id is a real numeric generation id.
  const markableId = resolveSlotGenerationId(slot.id, slot.asyncGenerationId);

  // Copy text content to clipboard
  const copyTextContent = async () => {
    if (slot?.textContent) {
      try {
        await navigator.clipboard.writeText(slot.textContent);
        setTextCopied(true);
        setTimeout(() => setTextCopied(false), 2000);
      } catch (error) {
        console.error('Copy text failed:', error);
      }
    }
  };

  const downloadImage = async () => {
    // Text content: download as .txt file
    if (slot.contentType === 'text' && slot.textContent) {
      const blob = new Blob([slot.textContent], { type: 'text/plain;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `generated-text-${slot.id}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 2000);
      return;
    }

    // Try to use the original unproxied URL if available (for gallery images)
    const downloadUrl = currentOriginalUnproxied || currentOriginalUrl || currentPreviewUrl;
    if (downloadUrl) {
      // Filename carries the media label + a plausible extension for the type
      // (a .glb model must not be saved as generated-image-42.jpg).
      const fileName = buildDownloadFilename(slot.contentType, slot.id, downloadUrl, selectedIndex + 1);
      try {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        setDownloadSuccess(true);
        setTimeout(() => setDownloadSuccess(false), 2000);
      } catch {
        // Fallback: open in new tab if fetch fails (e.g. CORS)
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const downloadImageWebP = async () => {
    const downloadUrl = currentOriginalUnproxied || currentOriginalUrl || currentPreviewUrl;
    if (downloadUrl) {
      try {
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        const webpBlob = await convertBlobToWebP(blob, 0.9);
        const blobUrl = URL.createObjectURL(webpBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `generated-image-${slot.id}-${selectedIndex + 1}.webp`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        setDownloadWebPSuccess(true);
        setTimeout(() => setDownloadWebPSuccess(false), 2000);
      } catch {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(slot.prompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const handleTrashClick = () => {
    if (onMoveToTrash && slot) {
      onMoveToTrash(parseInt(slot.id));
      // When slot navigation is active, the parent (GalleryContent) handles
      // advancing to the next image or closing the lightbox. Only close
      // explicitly when there's no slot navigation (standalone modal usage).
      if (!allSlots || allSlots.length === 0) {
        onClose();
      }
    }
  };

  const handleRemoveFromCollection = async (collectionId: number) => {
    if (onRemoveFromCollection && slot) {
      setRemovingCollectionId(collectionId);
      try {
        await onRemoveFromCollection(parseInt(slot.id), collectionId);
      } finally {
        setRemovingCollectionId(null);
      }
    }
  };

  const handleUpdateCollections = async (selectedIds: number[], initialIds: number[]) => {
    if (!slot) return;

    const imageId = parseInt(slot.id);

    // Calculate which collections to add (in selected but not in initial)
    const toAdd = selectedIds.filter(id => !initialIds.includes(id));
    // Calculate which collections to remove (in initial but not in selected)
    const toRemove = initialIds.filter(id => !selectedIds.includes(id));

    // Add to new collections
    if (toAdd.length > 0 && onAddToCollections) {
      await onAddToCollections(imageId, toAdd);
    }

    // Remove from unchecked collections
    if (toRemove.length > 0 && onRemoveFromCollection) {
      for (const collectionId of toRemove) {
        await onRemoveFromCollection(imageId, collectionId);
      }
    }

    setShowAddCollectionModal(false);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Stop propagation to prevent parent card from reopening the modal
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer"
      onClick={handleBackdropClick}
    >
      {/* Modal Content - Three column layout for multiple images: thumbnails | main image | info */}
      <div
        className="relative flex flex-col lg:flex-row max-w-[95vw] max-h-[95vh] min-w-[70vw] min-h-[70vh] bg-gray-900 rounded-lg overflow-hidden shadow-2xl cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left side: Thumbnails grid (only for multiple images) */}
        {hasMultipleImages && (
          <div className="w-full lg:w-24 flex-shrink-0 overflow-y-auto bg-gray-850 border-b lg:border-b-0 lg:border-r border-gray-700/50">
            <div className="flex lg:flex-col gap-1 p-2">
              {allPreviewUrls.map((url, index) => {
                const { assetUrl } = resolveAssetUrls(index);
                const thumbnailIs3D = resolveIs3D(slot?.contentType, assetUrl);

                return (
                  <button
                    key={index}
                    onClick={() => setSelectedIndex(index)}
                    className={`relative flex-shrink-0 w-16 h-16 lg:w-full lg:h-auto lg:aspect-square rounded overflow-hidden transition-all ${
                      selectedIndex === index
                        ? 'ring-2 ring-blue-500 ring-offset-1 ring-offset-gray-900'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    {thumbnailIs3D ? (
                      <Model3DPlaceholder url={assetUrl} language={language} compact />
                    ) : slot?.contentType?.startsWith('audio') ? (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/40 to-gray-800">
                        <Icon name={ICONS.AUDIO} size={20} className="text-purple-400" />
                      </div>
                    ) : slot?.contentType?.startsWith('video') ? (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-900/40 to-gray-800">
                        <Icon name={ICONS.VIDEO} size={20} className="text-green-400" />
                      </div>
                    ) : (
                      <img
                        src={url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {/* Index badge */}
                    <div className="absolute bottom-0.5 right-0.5 px-1 bg-black/70 rounded text-xs text-white">
                      {index + 1}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Center: Main image section */}
        <div className="flex-1 flex flex-col bg-black min-w-0">
          {/* Header - minimal with only navigation */}
          {(hasSlotNavigation || hasMultipleImages) && (
            <div className="flex items-center justify-between p-3 bg-gray-800/50 border-b border-gray-700/50">
              {/* Left: Slot navigation and/or image counter */}
              <div className="flex items-center gap-4">
                {/* Slot navigation (between different generations) */}
                {hasSlotNavigation && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={goToPreviousSlot}
                      className="p-1.5 bg-purple-600/60 hover:bg-purple-600 rounded-lg transition-colors"
                      title={t.sessionGallery?.previousGeneration || 'Previous generation (↑)'}
                    >
                      <Icon name={ICONS.CHEVRON_LEFT} size={18} className="text-white" />
                    </button>
                    <span className="text-sm text-purple-300 font-medium min-w-[3rem] text-center">
                      {(currentSlotIndex ?? 0) + 1} / {slotCount}
                      {isLoadingMore && <span className="ml-1 text-purple-400 animate-pulse">+</span>}
                    </span>
                    <button
                      onClick={goToNextSlot}
                      className="p-1.5 bg-purple-600/60 hover:bg-purple-600 rounded-lg transition-colors"
                      title={t.sessionGallery?.nextGeneration || 'Next generation (↓)'}
                    >
                      <Icon name={ICONS.CHEVRON_RIGHT} size={18} className="text-white" />
                    </button>
                  </div>
                )}

                {/* Image counter for multiple images within slot */}
                {hasMultipleImages && (
                  <div className="text-sm text-gray-400">
                    {selectedIndex + 1} / {imageCount} {
                      slot?.contentType?.startsWith('audio')
                        ? (t.galleryPage?.audioCount || 'audios')
                        : slot?.contentType?.startsWith('video')
                          ? (t.galleryPage?.videoCount || 'videos')
                          : (t.galleryPage?.imageCount || 'images')
                    }
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main media container with navigation arrows */}
          <div className="flex-1 flex items-center justify-center p-6 min-h-0 relative">
            {/* Left arrow for navigation */}
            {hasMultipleImages && (
              <button
                onClick={() => setSelectedIndex(prev => (prev > 0 ? prev - 1 : imageCount - 1))}
                className="absolute left-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
              >
                <Icon name={ICONS.CHEVRON_LEFT} size={24} className="text-current" />
              </button>
            )}

            {is3D ? (
              // Only surface in the app that mounts the real viewer — one WebGL
              // context at a time, lazily loaded so non-3D pages never pay for it.
              <div className="w-full h-full min-h-[50vh]" data-testid="model3d-viewer-host">
                <LazyModel3DViewer
                  key={`model-${selectedIndex}`}
                  url={model3dUrl}
                  language={language}
                  downloadFilename={buildDownloadFilename('3d', slot.id, model3dUrl, selectedIndex + 1)}
                  onDimensions={(dimensions) => {
                    setModelDimensions({ modelUrl: model3dUrl, value: dimensions });
                  }}
                />
              </div>
            ) : slot.contentType === 'text' ? (
              <div className="w-full h-full flex flex-col items-center justify-start p-8 overflow-y-auto">
                <div className="w-full max-w-3xl">
                  <pre className="text-sm text-gray-200 whitespace-pre-wrap break-words font-sans leading-relaxed">
                    {slot.textContent || (t.workflow?.textNoContent || 'No text content')}
                  </pre>
                </div>
              </div>
            ) : slot.contentType?.startsWith('audio') ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-8 p-8">
                <AudioWaveformPlaceholder size="large" className="scale-150" />
                <audio
                  key={`audio-${selectedIndex}`}
                  src={currentOriginalUrl || currentPreviewUrl}
                  className="w-full max-w-2xl"
                  controls
                  autoPlay
                />
              </div>
            ) : slot.contentType?.startsWith('video') ? (
              <video
                key={`video-${selectedIndex}`}
                src={currentOriginalUrl || currentPreviewUrl}
                className="max-w-full max-h-full object-contain rounded"
                controls
                autoPlay
                loop
                playsInline
              />
            ) : (
              <img
                src={currentOriginalUrl || currentPreviewUrl}
                alt="Generated image"
                className="max-w-full max-h-full object-contain rounded"
              />
            )}

            {/* Right arrow for navigation */}
            {hasMultipleImages && (
              <button
                onClick={() => setSelectedIndex(prev => (prev < imageCount - 1 ? prev + 1 : 0))}
                className="absolute right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
              >
                <Icon name={ICONS.CHEVRON_RIGHT} size={24} className="text-current" />
              </button>
            )}
          </div>
        </div>

        {/* Right side: Metadata section - scrollable */}
        {(slot.metadata || (is3D && modelStats)) && (
          <div className="w-full lg:w-96 flex-shrink-0 overflow-y-auto bg-gray-800 text-white border-t lg:border-t-0 lg:border-l border-gray-700/50">
            <div className="p-6 space-y-4">
              {/* Action buttons row with close button */}
              <div className="flex items-center justify-between pb-3 border-b border-gray-700/30">
                <div className="flex items-center gap-1.5">
                  {/* Download original quality button */}
                  <button
                    onClick={downloadImage}
                    className={`p-2 rounded-lg transition-colors ${
                      downloadSuccess
                        ? 'bg-green-600/30 text-green-400'
                        : 'bg-gray-700/60 text-gray-300 hover:bg-gray-600/80 hover:text-white'
                    }`}
                    title={downloadSuccess ? (t.gallery?.downloaded || 'Downloaded!') : (t.gallery?.downloadOriginal || 'Download original quality')}
                  >
                    <Icon name={downloadSuccess ? ICONS.CHECK : ICONS.DOWNLOAD} size={18} className="text-current" />
                  </button>

                  {/* Download WebP button - only for images */}
                  {!slot.contentType?.startsWith('video') && !slot.contentType?.startsWith('audio') && slot.contentType !== 'text' && !is3D && (
                    <button
                      onClick={downloadImageWebP}
                      className={`relative p-2 rounded-lg transition-colors ${
                        downloadWebPSuccess
                          ? 'bg-green-600/30 text-green-400'
                          : 'bg-gray-700/60 text-gray-300 hover:bg-gray-600/80 hover:text-white'
                      }`}
                      title={downloadWebPSuccess ? (t.gallery?.downloadedWebP || 'Downloaded WebP!') : (t.gallery?.downloadWebP || 'Download WebP')}
                    >
                      <Icon name={downloadWebPSuccess ? ICONS.CHECK : ICONS.DOWNLOAD} size={18} className="text-current" />
                      {!downloadWebPSuccess && (
                        <svg className="absolute top-0.5 right-0.5 w-2.5 h-2.5 text-yellow-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      )}
                    </button>
                  )}

                  {/* Copy text content button - only for text type */}
                  {slot.contentType === 'text' && slot.textContent && (
                    <button
                      onClick={copyTextContent}
                      className={`p-2 rounded-lg transition-colors ${
                        textCopied
                          ? 'bg-green-600/30 text-green-400'
                          : 'bg-gray-700/60 text-gray-300 hover:bg-gray-600/80 hover:text-white'
                      }`}
                      title={textCopied ? (t.gallery?.copied || 'Copied!') : (t.gallery?.copyText || 'Copy text')}
                    >
                      <Icon name={textCopied ? ICONS.CHECK : ICONS.COPY} size={18} className="text-current" />
                    </button>
                  )}

                  {/* Recreate button */}
                  {onRecreate && (
                    <button
                      onClick={() => {
                        onRecreate();
                        onClose();
                      }}
                      className="p-2 bg-gray-700/60 text-gray-300 hover:bg-gray-600/80 hover:text-white rounded-lg transition-colors"
                      title={t.gallery?.recreate || 'Recreate'}
                    >
                      <Icon name={ICONS.REFRESH} size={18} className="text-current" />
                    </button>
                  )}

                  {/* Trash button */}
                  {onMoveToTrash && (
                    <button
                      onClick={handleTrashClick}
                      className="p-2 bg-gray-700/60 text-gray-300 hover:bg-red-600/40 hover:text-red-300 rounded-lg transition-colors"
                      title={t.gallery?.moveToTrash || 'Move to trash'}
                    >
                      <Icon name={ICONS.TRASH} size={18} className="text-current" />
                    </button>
                  )}

                  {/* Draft → 1080p final */}
                  {onDraftFinal && isDraftFinalCandidate(slot) && (
                    <button
                      type="button"
                      onClick={() => onDraftFinal(slot)}
                      className="p-2 bg-gray-700/60 text-purple-300 hover:bg-purple-600/40 hover:text-white rounded-lg transition-colors"
                      title={t.draftFinal.button}
                      aria-label={t.draftFinal.button}
                    >
                      <Icon name={ICONS.SPARKLES} size={18} className="text-current" />
                    </button>
                  )}

                  {/* Mark buttons (like / dislike / approved) */}
                  {onMark && markableId !== null && (
                    <div className="flex items-center pl-1 ml-1 border-l border-gray-700/40">
                      <MarkButtons
                        mark={slot.mark}
                        onMark={(next) => onMark(slot.id, next)}
                        language={language}
                        size={18}
                      />
                    </div>
                  )}
                </div>

                {/* Close button */}
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  title={t.modal?.close || 'Close'}
                >
                  <Icon name={ICONS.X} size={20} className="text-current" />
                </button>
              </div>

              {/* Prompt */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-gray-400 font-medium">{t.galleryPage?.prompt || 'Prompt'}</span>
                  {slot.prompt && slot.prompt !== 'No prompt' && (
                    <button
                      onClick={copyPrompt}
                      className="p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
                      title={promptCopied ? (t.gallery?.promptCopied || 'Copied!') : (t.gallery?.copyPrompt || 'Copy prompt')}
                    >
                      <Icon name={promptCopied ? ICONS.CHECK : ICONS.COPY} size={14} className={promptCopied ? 'text-green-400' : 'text-current'} />
                    </button>
                  )}
                </div>
                <div className="text-base leading-relaxed text-gray-200">{slot.prompt}</div>
              </div>

              {/* References from Prompt V2 - Compact display like Prompt Editor V2 */}
              {(promptV2Summary.chips.length > 0 || promptV2Summary.allImages.length > 0) && (
                <div className="pt-3 border-t border-gray-700/30">
                  <div className="text-sm text-gray-400 mb-3 font-medium">
                    {t.galleryPage?.references || 'References'}
                  </div>

                  {/* Summary chips - compact display */}
                  {promptV2Summary.chips.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {promptV2Summary.chips.map((chip, idx) => (
                        <div
                          key={idx}
                          className="inline-flex items-center bg-purple-900/40 border border-purple-600/30 rounded-full px-2.5 py-1 text-xs"
                        >
                          <span className="text-purple-300 font-medium">{chip.label}</span>
                          {chip.values.length > 0 && (
                            <>
                              <span className="text-purple-300 font-medium">:</span>
                              <span className="text-gray-300 ml-1">{chip.values.join(', ')}</span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reference Images - just thumbnails with hold to zoom */}
                  {promptV2Summary.allImages.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-gray-500">
                          {t.galleryPage?.referenceImages || 'Reference Images'}
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                          <span className="text-[10px]">{t.galleryPage?.holdToZoom || 'Hold to zoom'}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {promptV2Summary.allImages.map((url, idx) => (
                          <img
                            key={idx}
                            src={url}
                            alt=""
                            className="w-10 h-10 object-cover rounded border border-gray-700 hover:border-purple-500/50 transition-colors cursor-zoom-in select-none"
                            onMouseDown={(e) => handleRefImagePressStart(e, url)}
                            onMouseUp={(e) => handleRefImagePressEnd(e)}
                            onMouseLeave={(e) => handleRefImagePressEnd(e)}
                            onTouchStart={(e) => handleRefImagePressStart(e, url)}
                            onTouchEnd={(e) => handleRefImagePressEnd(e)}
                            onClick={(e) => e.stopPropagation()}
                            draggable={false}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Generation Parameters */}
              {generationParams.length > 0 && (
                <div className="pt-3 border-t border-gray-700/30">
                  <div className="text-xs text-gray-500 mb-2">
                    {t.galleryPage?.generationParams || 'Generation Parameters'}
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {generationParams.map((param) => (
                      <div key={param.key} className="min-w-0">
                        <div className="text-[10px] text-gray-400 truncate" title={param.label}>{param.label}</div>
                        <div className="text-xs text-white truncate" title={param.value}>{param.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Model Info */}
              {is3D && modelStats && (
                <div className="pt-3 border-t border-gray-700/30">
                  <div className="text-xs text-gray-500 mb-2">
                    {t.model3d?.infoTitle || 'Model Info'}
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {[
                      { label: t.model3d?.vertices || 'Vertices', value: modelStats.vertices.toLocaleString() },
                      { label: t.model3d?.triangles || 'Triangles', value: modelStats.triangles.toLocaleString() },
                      { label: t.model3d?.meshes || 'Meshes', value: modelStats.meshes.toLocaleString() },
                      { label: t.model3d?.materials || 'Materials', value: modelStats.materials.toLocaleString() },
                      { label: t.model3d?.textures || 'Textures', value: modelStats.textures.toLocaleString() },
                      {
                        label: t.model3d?.fileSize || 'File size',
                        value: modelStats.fileSize / 1024 / 1024 >= 0.01
                          ? `${(modelStats.fileSize / 1024 / 1024).toFixed(2)} MB`
                          : `${(modelStats.fileSize / 1024).toFixed(2)} KB`,
                      },
                      ...(currentModelDimensions ? [{
                        label: t.model3d?.dimensions || 'Dimensions',
                        value: `${currentModelDimensions.x.toFixed(2)} × ${currentModelDimensions.y.toFixed(2)} × ${currentModelDimensions.z.toFixed(2)} m`,
                      }] : []),
                    ].map((item) => (
                      <div key={item.label} className="min-w-0">
                        <div className="text-[10px] text-gray-400 truncate" title={item.label}>{item.label}</div>
                        <div className="text-xs text-white truncate" title={item.value}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata grid */}
              <div className="space-y-4 pt-3 border-t border-gray-700/30">
                {/* Generation ID */}
                {slot.id && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t.galleryPage?.generationId || 'Generation ID'}</div>
                    <div className="flex items-center gap-2">
                      <code className="text-sm text-white font-mono select-all">{slot.id}</code>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(String(slot.id))}
                        className="p-1 rounded hover:bg-gray-700/50 text-gray-400 hover:text-white transition-colors"
                        title={t.galleryPage?.copyId || 'Copy ID'}
                      >
                        <Icon name={ICONS.COPY} size={14} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Media count for multiple items */}
                {hasMultipleImages && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{
                      slot?.contentType?.startsWith('audio')
                        ? (t.galleryPage?.audioCount || 'Audios')
                        : slot?.contentType?.startsWith('video')
                          ? (t.galleryPage?.videoCount || 'Videos')
                          : (t.galleryPage?.imageCount || 'Images')
                    }</div>
                    <div className="text-sm text-purple-300">{imageCount}</div>
                  </div>
                )}

                {/* Author */}
                {slot.metadata?.userEmail && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t.galleryPage?.createdBy || 'Created by'}</div>
                    <div className="text-sm text-white">{slot.metadata.userEmail}</div>
                  </div>
                )}

                {/* Workflow */}
                {slot.metadata?.workflowName && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t.galleryPage?.workflow || 'Workflow'}</div>
                    <div className="text-sm text-white">{slot.metadata.workflowName}</div>
                  </div>
                )}

                {/* Creation time */}
                {slot.metadata?.createdAt && (
                  <div>
                    <div className="text-xs text-gray-400 mb-1">{t.galleryPage?.createdAt || 'Created'}</div>
                    <div className="text-sm text-white">
                      {new Date(slot.metadata.createdAt).toLocaleString(language, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                )}

                {/* Collections as tags */}
                {(parsedCollections.length > 0 || collectionsRawString || onAddToCollections) && (
                  <div>
                    <div className="text-xs text-gray-400 mb-2">{t.galleryPage?.collections || 'Collections'}</div>
                    <div className="flex flex-wrap gap-2">
                      {parsedCollections.length > 0 ? (
                        <>
                          {parsedCollections.map((collection) => (
                            <div
                              key={collection.id}
                              className="group relative inline-flex items-center bg-purple-900/50 border border-purple-600/30 rounded-full px-3 py-1 text-sm text-purple-200 hover:bg-purple-800/50 transition-colors"
                            >
                              <span>{collection.name}</span>
                              {/* Remove button - only for non-default collections and if more than one collection */}
                              {!collection.is_default && onRemoveFromCollection && parsedCollections.length > 1 && (
                                <button
                                  onClick={() => handleRemoveFromCollection(collection.id)}
                                  disabled={removingCollectionId === collection.id}
                                  className="ml-1.5 -mr-1 p-0.5 rounded-full opacity-0 group-hover:opacity-100 hover:bg-purple-600/50 transition-all disabled:opacity-50"
                                  title={t.gallery?.removeFromCollection || 'Remove from collection'}
                                >
                                  {removingCollectionId === collection.id ? (
                                    <div className="w-3 h-3 animate-spin rounded-full border border-purple-300 border-t-transparent" />
                                  ) : (
                                    <Icon name={ICONS.X} size={12} className="text-purple-300" />
                                  )}
                                </button>
                              )}
                            </div>
                          ))}
                        </>
                      ) : collectionsRawString ? (
                        /* Fallback: show raw string if parsing failed */
                        <div className="text-sm text-purple-300">{collectionsRawString}</div>
                      ) : null}

                      {/* Add to collection button */}
                      {onAddToCollections && collections.length > 0 && (
                        <button
                          onClick={() => setShowAddCollectionModal(true)}
                          className="inline-flex items-center gap-1 bg-gray-700/50 border border-gray-600/30 rounded-full px-3 py-1 text-sm text-gray-300 hover:bg-gray-600/50 hover:text-white transition-colors"
                        >
                          <Icon name={ICONS.PLUS} size={12} />
                          <span>{t.common?.add || 'Add'}</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Collection Select Modal */}
      {showAddCollectionModal && (
        <CollectionSelectModal
          isOpen={showAddCollectionModal}
          onClose={() => setShowAddCollectionModal(false)}
          onSubmit={handleUpdateCollections}
          collections={collections.filter(c => c.can_edit)}
          selectedCollectionIds={parsedCollections.map(c => c.id)}
          selectedLanguage={language}
        />
      )}

      {/* Reference Image Zoom Preview - Hold to zoom (rendered inside same portal) */}
      {zoomPreviewUrl && (
        <div
          className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 cursor-pointer"
          onClick={(e) => handleCloseZoomPreview(e)}
          onMouseUp={(e) => handleCloseZoomPreview(e)}
          onTouchEnd={(e) => handleCloseZoomPreview(e)}
        >
          <div className="bg-gray-900 border-2 border-purple-500/50 rounded-2xl shadow-2xl shadow-purple-500/20 p-3 animate-in fade-in zoom-in-95 duration-200">
            <img
              src={zoomPreviewUrl}
              alt=""
              className="max-w-[70vw] max-h-[70vh] object-contain rounded-xl"
            />
            <div className="text-center mt-2 text-gray-400 text-xs">
              {language === 'ru' ? 'Отпустите для закрытия' : 'Release to close'}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
